import { useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export function AnimatedGridPattern({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  maxOpacity = 0.1,
  duration = 5,
  repeatDelay = 0,
  className,
  ...props
}) {
  const id = useId();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [squares, setSquares] = useState(() => generateSquares(numSquares, 1, 1));

  function getPos(cols, rows) {
    return [
      Math.floor((Math.random() * cols)),
      Math.floor((Math.random() * rows)),
    ];
  }

  function generateSquares(count, cols, rows) {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      pos: getPos(cols, rows),
    }));
  }

  const updateSquarePosition = (id) => {
    setSquares((current) =>
      current.map((sq) => {
        if (sq.id === id) {
          const cols = Math.floor(dimensions.width / width);
          const rows = Math.floor(dimensions.height / height);
          return { ...sq, pos: getPos(cols, rows) };
        }
        return sq;
      })
    );
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        setDimensions({ width: w, height: h });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (dimensions.width && dimensions.height) {
      const cols = Math.floor(dimensions.width / width);
      const rows = Math.floor(dimensions.height / height);
      setSquares(generateSquares(numSquares, cols, rows));
    }
  }, [dimensions, numSquares, width, height]);

  const cols = Math.floor(dimensions.width / width);
  const rows = Math.floor(dimensions.height / height);

  return (
    <svg
      ref={containerRef}
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 h-full w-full fill-gray-400/20 stroke-gray-400/20',
        className
      )}
      {...props}
    >
      <defs>
        <pattern
          id={id}
          width={width}
          height={height}
          patternUnits="userSpaceOnUse"
          x={x}
          y={y}
        >
          <path
            d={`M.5 ${height}V.5H${width}`}
            fill="none"
            strokeDasharray={strokeDasharray}
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
      <svg x={x} y={y} className="overflow-visible">
        {squares.map(({ pos: [sqX, sqY], id: squareId }) => (
          <motion.rect
            key={`${squareId}-${sqX}-${sqY}`}
            width={width - 1}
            height={height - 1}
            x={sqX * width + 1}
            y={sqY * height + 1}
            fill="currentColor"
            strokeWidth="0"
            initial={{ opacity: 0 }}
            animate={{ opacity: maxOpacity }}
            transition={{
              duration,
              repeat: Infinity,
              delay: squareId * 0.05,
              repeatType: 'reverse',
              repeatDelay,
              ease: 'easeInOut',
            }}
            onAnimationComplete={() => updateSquarePosition(squareId)}
          />
        ))}
      </svg>
    </svg>
  );
}

export default AnimatedGridPattern;
